import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState
} from 'react';
import {
  updateCache,
  useKeyValueSettingsMutation
} from '~/modules/common/hooks';
import { useMeContext } from '~/modules/me/useMeContext';
import { resourcingLegendItemsV2 } from '~/modules/resourcing/common/components/ResourcingLegends/resourcingLegendItems';
import {
  DEFAULT_DISPLAY_UNIT,
  DEFAULT_PERIOD_SCALE,
  PERIOD_SCALE_ENUM
} from '~/modules/resourcing/common/enums';
import { useGetChartDates } from './useGetResourceChartInformation';

const getRequestStatusListV2 = ({
  isResourceActualModeEnabled,
  isPsaRmpTaskAllocation1Enabled
}) => ({
  none: [resourcingLegendItemsV2.notWorking],
  resourceRequests: [
    resourcingLegendItemsV2.request,
    resourcingLegendItemsV2.requestRejected,
    resourcingLegendItemsV2.toBeHired,
    resourcingLegendItemsV2.resourceRejected,
    resourcingLegendItemsV2.pending,
    resourcingLegendItemsV2.proposed
  ],
  resources: [
    resourcingLegendItemsV2.complete,
    isPsaRmpTaskAllocation1Enabled && resourcingLegendItemsV2.overAllocated,
    isResourceActualModeEnabled && resourcingLegendItemsV2.actuals
  ].filter(ele => ele)
});

export const ResourcingChartToolbarContext = createContext();
const useResourcingChartToolbarContext = () =>
  useContext(ResourcingChartToolbarContext);

export const useResourcingChartToolbarContextValue = ({
  width,
  setSearchCriteria,
  searchCriteria,
  filter,
  sort,
  onSortChange,
  resourceRequestsMinMaxDateAndStatuses,
  resourceAllocationsMinMaxDateAndStatuses,
  chartSettings,
  project
}) => {
  const [scale, setScale] = useState(
    chartSettings?.scale || DEFAULT_PERIOD_SCALE
  );

  const [displayUnit, setDisplayUnit] = useState(
    chartSettings?.displayUnit || DEFAULT_DISPLAY_UNIT
  );

  const [
    isResourceActualModeEnabled,
    setIsResourceActualModeEnabled
  ] = useState(chartSettings?.showActuals || false);

  const {
    featureFlags: {
      isPsaRmpTaskAllocation1Enabled,
      isPsaRmpSetResourceAllocationEnabled,
      isRmp2025Q4Enhancements,
      isRmp2026Q1Enhancements
    }
  } = useMeContext();

  const isPsaRmpSetResourceAllocationFlowEnabled =
    isPsaRmpTaskAllocation1Enabled && isPsaRmpSetResourceAllocationEnabled;

  const shouldUseScheduleBasedBackgroundImage =
    isRmp2026Q1Enhancements &&
    (scale === PERIOD_SCALE_ENUM.DAYS || scale === PERIOD_SCALE_ENUM.WEEKS);

  const [putKeyValueSettings] = useKeyValueSettingsMutation(
    updateCache('resource_plan_chart_settings')
  );

  const handleSettingsChange = useCallback(
    ({
      displayUnit: newDisplayUnit,
      showActuals: newIsResourceActualModeEnabled,
      scale: newScale
    }) => {
      putKeyValueSettings({
        variables: {
          input: {
            key: 'resource_plan_chart_settings',
            settings: {
              displayUnit: newDisplayUnit,
              showActuals: newIsResourceActualModeEnabled,
              scale: newScale
            }
          }
        }
      });
    },
    [putKeyValueSettings]
  );

  const legends = getRequestStatusListV2({
    isResourceActualModeEnabled,
    isPsaRmpTaskAllocation1Enabled
  });

  const {
    chartDates,
    onPrevious,
    onNext,
    dateRange,
    setFixedRangeScale,
    setDate
  } = useGetChartDates({
    width,
    scale,
    resourceRequestsMinMaxDateAndStatuses,
    resourceAllocationsMinMaxDateAndStatuses,
    sessionStorageKey: `resource-plan-chart-start-date-${project.id}`
  });

  const onScaleChange = useCallback(
    newValue => {
      setScale(newValue);
      setFixedRangeScale(newValue);
      handleSettingsChange({
        displayUnit,
        showActuals: isResourceActualModeEnabled,
        scale: newValue
      });
    },
    [
      displayUnit,
      handleSettingsChange,
      isResourceActualModeEnabled,
      setFixedRangeScale
    ]
  );

  const onSetDisplayUnit = useCallback(
    newValue => {
      setDisplayUnit(newValue);
      handleSettingsChange({
        displayUnit: newValue,
        showActuals: isResourceActualModeEnabled,
        scale
      });
    },
    [handleSettingsChange, isResourceActualModeEnabled, scale]
  );

  const onSetIsResourceActualModeEnabled = useCallback(
    newValue => {
      setIsResourceActualModeEnabled(newValue);
      handleSettingsChange({
        displayUnit,
        showActuals: newValue,
        scale
      });
    },
    [displayUnit, handleSettingsChange, scale]
  );

  return useMemo(
    () => ({
      displayUnit,
      setDisplayUnit: onSetDisplayUnit,
      scale,
      chartDates,
      dateRange,
      setScale: onScaleChange,
      onPrevious,
      onNext,
      isResourceActualModeEnabled,
      setIsResourceActualModeEnabled: onSetIsResourceActualModeEnabled,
      legends,
      setSearchCriteria,
      searchCriteria,
      filter,
      sort,
      onSortChange,
      handleSettingsChange,
      setDate,
      isPsaRmpSetResourceAllocationFlowEnabled,
      isRmp2025Q4Enhancements,
      isRmp2026Q1Enhancements,
      shouldUseScheduleBasedBackgroundImage
    }),
    [
      displayUnit,
      onSetDisplayUnit,
      scale,
      chartDates,
      dateRange,
      onScaleChange,
      onPrevious,
      onNext,
      isResourceActualModeEnabled,
      onSetIsResourceActualModeEnabled,
      legends,
      setSearchCriteria,
      searchCriteria,
      filter,
      sort,
      onSortChange,
      handleSettingsChange,
      setDate,
      isPsaRmpSetResourceAllocationFlowEnabled,
      isRmp2025Q4Enhancements,
      isRmp2026Q1Enhancements,
      shouldUseScheduleBasedBackgroundImage
    ]
  );
};

export default useResourcingChartToolbarContext;
